import React, { useEffect, useState } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { db } from '../firebaseConfig';
import { doc, getDoc, updateDoc, collection, query, where, getDocs, limit } from 'firebase/firestore';
import Navbar from './Navbar';
import '../styles/blog.css';
import Seo from '../components/Seo';
import DOMPurify from 'dompurify';

const normalizeCategory = (value = '') => {
  const text = value.toLowerCase();
  if (text.includes('thibb') || text.includes('nabawi')) return 'Thibbun Nabawi';
  if (text.includes('gangguan') || text.includes('keluhan') || text.includes('jin') || text.includes('sihir')) {
    return 'Gangguan & Keluhan';
  }
  if (text.includes('edukasi') || text.includes('ruqyah')) return 'Edukasi Ruqyah';
  if (text.includes('umum')) return 'Umum';
  return value ? 'Umum' : '';
};

export default function BlogDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [article, setArticle] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [shareMessage, setShareMessage] = useState('');

  const renderContentBlocks = (content = '') => {
    const lines = content.split(/\r?\n/);
    const blocks = [];
    let listItems = [];

    const flushList = () => {
      if (listItems.length) {
        blocks.push({ type: 'ul', items: listItems });
        listItems = [];
      }
    };

    lines.forEach(line => {
      const text = line.trim();
      if (!text) {
        flushList();
        return;
      }
      if (text.startsWith('- ')) {
        listItems.push(text.slice(2));
        return;
      }
      flushList();
      if (text.startsWith('### ')) {
        blocks.push({ type: 'h3', text: text.slice(4) });
      } else if (text.startsWith('## ')) {
        blocks.push({ type: 'h2', text: text.slice(3) });
      } else if (text.startsWith('> ')) {
        blocks.push({ type: 'quote', text: text.slice(2) });
      } else {
        blocks.push({ type: 'p', text });
      }
    });

    flushList();

    return blocks.map((block, idx) => {
      if (block.type === 'h2') return <h2 key={`h2-${idx}`}>{block.text}</h2>;
      if (block.type === 'h3') return <h3 key={`h3-${idx}`}>{block.text}</h3>;
      if (block.type === 'quote') return <blockquote key={`q-${idx}`}>{block.text}</blockquote>;
      if (block.type === 'ul') {
        return (
          <ul key={`ul-${idx}`}>
            {block.items.map((item, itemIdx) => (
              <li key={`li-${idx}-${itemIdx}`}>{item}</li>
            ))}
          </ul>
        );
      }
      return <p key={`p-${idx}`}>{block.text}</p>;
    });
  };

  const renderHtmlContent = (content = '') => {
    const safeHtml = DOMPurify.sanitize(content, {
      USE_PROFILES: { html: true }
    });
    return <div dangerouslySetInnerHTML={{ __html: safeHtml }} />;
  };

  const shareUrl = typeof window !== 'undefined' ? window.location.href : '';
  const formattedDate =
    article?.date?.seconds
      ? new Date(article.date.seconds * 1000).toLocaleDateString('id-ID')
      : '-';
  const displayCategory = article ? normalizeCategory(article.category) || 'Umum' : '';
  const siteUrl =
    (process.env.REACT_APP_SITE_URL ||
      (typeof window !== 'undefined' ? window.location.origin : '') ||
      '').replace(/\/$/, '');

  const handleCopyLink = async () => {
    if (!shareUrl) return;
    try {
      await navigator.clipboard.writeText(shareUrl);
      setShareMessage('Link disalin');
      setTimeout(() => setShareMessage(''), 2000);
    } catch (err) {
      setShareMessage('Gagal menyalin link');
      setTimeout(() => setShareMessage(''), 2000);
    }
  };

  useEffect(() => {
    const fetchArticle = async () => {
      try {
        let docSnap = null;
        let docId = null;

        const byIdRef = doc(db, 'articles', id);
        const byIdSnap = await getDoc(byIdRef);
        if (byIdSnap.exists()) {
          const data = byIdSnap.data();
          if (data.slug && data.slug !== id) {
            navigate(`/post/${data.slug}`, { replace: true });
            return;
          }
          docSnap = byIdSnap;
          docId = byIdSnap.id;
        } else {
          const slugQuery = query(
            collection(db, 'articles'),
            where('slug', '==', id),
            where('status', '==', 'published'),
            limit(1)
          );
          const slugSnap = await getDocs(slugQuery);
          if (!slugSnap.empty) {
            docSnap = slugSnap.docs[0];
            docId = docSnap.id;
          }
        }

        if (docSnap && docSnap.exists() && docSnap.data().status === 'published') {
          const data = docSnap.data();
          const docRef = doc(db, 'articles', docId);
          const hasViewsField = Object.prototype.hasOwnProperty.call(data, 'views');
          const numericViews =
            typeof data.views === 'number' && Number.isFinite(data.views)
              ? data.views
              : Number.parseInt(data.views, 10);
          const safeViews = Number.isFinite(numericViews) ? numericViews : 0;
          const canUpdateViews = !hasViewsField || Number.isInteger(safeViews);
          const nextViews = safeViews + 1;

          try {
            if (canUpdateViews) {
              await updateDoc(docRef, {
                views: nextViews,
              });
            }
          } catch (viewErr) {
            // Jangan gagalkan halaman jika update view ditolak rule.
            console.warn('View update skipped:', viewErr?.message || viewErr);
          }

          setArticle({ id: docId, ...data, views: safeViews });
        } else {
          setError('Artikel tidak ditemukan atau belum diterbitkan.');
        }
      } catch (err) {
        setError('Gagal memuat artikel: ' + err.message);
      } finally {
        setLoading(false);
      }
    };

    fetchArticle();
  }, [id, navigate]);

  const articleJsonLd = article
    ? {
        '@context': 'https://schema.org',
        '@type': 'Article',
        headline: article.title,
        description: article.excerpt || 'Artikel ruqyah syar\'i dan kesehatan ruhani.',
        image: article.image ? [article.image] : undefined,
        author: {
          '@type': 'Organization',
          name: "Ruqyah Syar'i Tegal"
        },
        publisher: {
          '@type': 'Organization',
          name: "Ruqyah Syar'i Tegal"
        },
        datePublished: article.date?.seconds
          ? new Date(article.date.seconds * 1000).toISOString()
          : undefined,
        dateModified: article.updatedAt?.seconds
          ? new Date(article.updatedAt.seconds * 1000).toISOString()
          : undefined,
        mainEntityOfPage: {
          '@type': 'WebPage',
          '@id': `${siteUrl}/post/${article.slug || article.id}`
        }
      }
    : null;

  return (
    <>
      {article && (
        <Seo
          title={article.title}
          description={article.excerpt || "Artikel ruqyah syar'i dan kesehatan ruhani."}
          canonical={`/post/${article.slug || article.id}`}
          image={article.image}
          type="article"
          jsonLd={articleJsonLd}
        />
      )}
      <Navbar />

      {loading && (
        <section className="section bg-white">
          <div className="container py-5">
            <p className="text-center">Memuat artikel...</p>
          </div>
        </section>
      )}

      {error && !loading && (
        <section className="section bg-white">
          <div className="container py-5">
            <p className="text-center text-danger">{error}</p>
          </div>
        </section>
      )}

      {!loading && !error && article && (
        <>
          <section
            className="article-hero"
            style={
              article.image
                ? { backgroundImage: `url(${article.image})` }
                : undefined
            }
          >
            <div className="article-hero-overlay"></div>
            <div className="container article-hero-content">
              <Link to="/blog" className="btn btn-sm btn-outline-light article-back">
                ← Kembali ke Blog
              </Link>
              {displayCategory && (
                <span className="article-category-badge">{displayCategory}</span>
              )}
              <h1 className="article-title">{article.title}</h1>
              {article.excerpt && <p className="article-excerpt">{article.excerpt}</p>}
              <div className="article-meta-row">
                <span>
                  <i className="bi bi-calendar3"></i>
                  {formattedDate}
                </span>
                <span className="dot">•</span>
                <span>
                  <i className="bi bi-eye"></i>
                  {article.views || 0} views
                </span>
              </div>
              <div className="article-share">
                <button className="btn btn-light btn-sm" onClick={handleCopyLink}>
                  <i className="bi bi-link-45deg"></i>
                  Salin Link
                </button>
                <a
                  className="btn btn-success btn-sm"
                  href={`https://wa.me/?text=${encodeURIComponent(
                    `${article.title} - ${shareUrl}`
                  )}`}
                  target="_blank"
                  rel="noreferrer"
                >
                  <i className="bi bi-whatsapp"></i>
                  Bagikan WhatsApp
                </a>
                {shareMessage && <span className="share-toast">{shareMessage}</span>}
              </div>
            </div>
          </section>

          <section className="section bg-white">
            <div className="container">
              <article className="article-content">
                {/<\/?[a-z][\s\S]*>/i.test(article.content || '')
                  ? renderHtmlContent(article.content || '')
                  : renderContentBlocks(article.content || '')}
              </article>
            </div>
          </section>
        </>
      )}
    </>
  );
}
